import React, { useEffect, useContext, useState, useRef, useCallback } from "react";
import { renderToString } from "react-dom/server";
import { NavLink } from "react-router-dom";
import Moment from "react-moment";
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import { MdDelete } from "react-icons/md";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Modal } from "react-bootstrap";

// Contexts
import { AuditContext } from "../../../../contexts/Audit";
// Functions & Components
import { BASE_URL } from "../../../../BaseUrl";
import { _t, getCookie, checkPermission, getSystemSettings, generatePdfHtmlTemplate } from "../../../../functions/Functions";
import DataTable from "../../../common/DataTable";
import AuditSidebar from "./AuditSidebar";
import { ToastSuccess, ToastError } from "../../../../functions/ToastShow";
import { UserContext } from "../../../../contexts/User";
import { SettingsContext } from "../../../../contexts/Settings";
import RcrReportTemplate from "../reports/RcrReportTemplate";

const RcrList = () => {
  const { t } = useTranslation();

  const {
    getPaginatedRcrAudits,
    rcrAuditList,
    rcrAuditListForSearch,
    setRcrAuditList,
    setRcrAuditListForSearch,
    loading,
    dataPaginating,
    setDataPaginating,
  } = useContext(AuditContext);

  const { authUserInfo } = useContext(UserContext)
  const { generalSettings } = useContext(SettingsContext);

  const [searchedRcrAudit, setSearchedRcrAudit] = useState({
    list: null,
    searched: false,
  });

  const searchRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [currentAuditId, setCurrentAuditId] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  const handleGenerateReportClick = async (auditId) => {
    setDataPaginating(true);
    
    let apiUrl = `${BASE_URL}/settings/dashboard-report?audit_id=${auditId}`;
    try {
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      const data = response.data;
      if (data?.audit_data) {
        setModalData(data);
        setCurrentAuditId(auditId);
        setShowModal(true);
      } else {
        ToastError({ title: "Failed to fetch report data" });
      }
    } catch (error) {
      ToastError({ title: "Error fetching report" });
    } finally {
      setDataPaginating(false);
    }
  };

  const submitGenerateReport = async () => {
    setIsSubmittingReport(true);
    try {
      const reportHtml = renderToString(
        <RcrReportTemplate 
          reportData={modalData} 
          generalSettings={generalSettings} 
        />
      );
      
      const fullHtmlTemplate = generatePdfHtmlTemplate(reportHtml, "RCR Report");

      const payload = {
        audit_id: currentAuditId,
        audit_features: modalData?.qualitative?.audit_features || "",
        appreciations_improvements: modalData?.qualitative?.appreciations_improvements || "",
        exceptions: modalData?.qualitative?.exceptions || "",
        html: fullHtmlTemplate
      };

      const postResponse = await axios.post(`${BASE_URL}/settings/update-audit-columns`, payload,
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      );

      if (postResponse.status === 200) {
        ToastSuccess({ title: "Report generated and updated successfully" });
        setShowModal(false);
        getPaginatedRcrAudits(1);
      } else {
        ToastError({ title: "Failed to update audit columns" });
      }
    } catch (error) {
      ToastError({ title: "Error generating report" });
    } finally {
      setIsSubmittingReport(false);
    }
  };

  useEffect(() => {
    getPaginatedRcrAudits(1);
  }, []);

  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedRcrAudit({ ...searchedRcrAudit, searched: false });
    } else {
      let searchedList = rcrAuditListForSearch?.filter((item) => {
        let lowerCaseBranch = item.branch?.name?.toLowerCase() || "";
        let lowerCaseSection = item.section?.name?.toLowerCase() || "";
        return (
          lowerCaseBranch.includes(searchInput) ||
          lowerCaseSection.includes(searchInput)
        );
      }) || [];
      
      setSearchedRcrAudit({
        list: searchedList,
        searched: true,
      });
    }
  };

  const handleFetchData = (page) => {
    getPaginatedRcrAudits(page);
  };

  const getListData = useCallback(() => {
    if (searchedRcrAudit.searched) {
      return searchedRcrAudit.list;
    }
    return rcrAuditList?.data || [];
  }, [searchedRcrAudit, rcrAuditList]);

  const onClearSearch = () => {
    setSearchedRcrAudit({ ...searchedRcrAudit, searched: false });
    if (searchRef.current) {
      searchRef.current.value = "";
    }
  };

  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this audit?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleDeleteAudit(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handleDeleteAudit = async (id) => {
    setDataPaginating(true);
    const url = BASE_URL + `/api/rcr/audit/${id}`;
    try {
      const res = await axios.post(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      setRcrAuditList(res.data[0]);
      setRcrAuditListForSearch(res.data[1]);
      setSearchedRcrAudit({
        ...searchedRcrAudit,
        list: res.data[1],
      });
      setDataPaginating(false);
      ToastSuccess({ title: `${_t(t("Audit has been deleted"))}` });
    } catch (error) {
      setDataPaginating(false);
      ToastError({ title: `${_t(t("Please try again"))}` });
    }
  };

  const COLUMNS = [
    { 
      label: "S/L", 
      render: (item, i) => searchedRcrAudit.searched
        ? i + 1
        : i + 1 + ((rcrAuditList?.current_page || 1) - 1) * (rcrAuditList?.per_page || 0)
    },
    { label: "Code", render: (item) => item?.audit_code ?? "-" },
    { label: "Date", render: (item) => <Moment format="LL">{item.audit_date}</Moment> },
    { label: "Branch", render: (item) => <>{item.branch?.name || item.branch_name}</> },
    // { label: "Section", render: (item) => <>{item.section?.name || item.section_name}</> },
    { label: "Weighted Score", render: (item) => item.overall_weighted_score ?? "-" },
    { label: "Status", render: (item) => item.status ?? "-" },
    {
      label: "Action", render: (item) => (
        <div className="d-flex justify-content-center">
          {
            item.status !== "completed" && (
              <NavLink
                title="Edit"
                className="btn btn-primary ml-2 rounded-sm"
                to={`/dashboard/audit/rcr-edit/${item.id}`}
                style={{ height: "1.7rem", width: "1.7rem" }}
              >
                <div className="d-flex align-items-center justify-content-center">
                  <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
                </div>
              </NavLink>
            )
          }
          {
            authUserInfo.permissions !== null &&
              checkPermission(
                authUserInfo.permissions,
                "RCR Audit Report"
              ) ? (
                <>
                  <NavLink
                    title="View Report"
                    className="btn btn-info ml-2 rounded-sm"
                    to={`/dashboard/rcr-reports/${item.id}`}
                    style={{ height: "1.7rem", width: "1.7rem" }}
                  >
                    <div className="d-flex align-items-center justify-content-center">
                      <i className="fa fa-eye" aria-hidden="true" style={{ fontSize: '14px', color: 'white' }}></i>
                    </div>
                  </NavLink>
                  {
                    item?.status === "in_review" && (
                      <button
                        type="button"
                        title="Generate Report"
                        className="btn btn-warning ml-2 rounded-sm border-0"
                        onClick={() => handleGenerateReportClick(item.id)}
                        style={{ height: "1.7rem", width: "1.7rem" }}
                      >
                        <div className="d-flex align-items-center justify-content-center">
                          <i className="fa fa-file-text-o" aria-hidden="true" style={{ fontSize: '14px', color: 'white' }}></i>
                        </div>
                      </button>
                    )
                  }
                </>
              ) : ""
          }
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>{_t(t("Audits"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <AuditSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                  <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                    <ul className="t-list fk-breadcrumb">
                      <li className="fk-breadcrumb__list">
                        <span className="t-link fk-breadcrumb__link text-capitalize">
                          {!searchedRcrAudit.searched
                            ? _t(t("Audit List"))
                            : _t(t("Search Result"))}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-6 col-lg-7">
                    <div className="row gx-3 align-items-center">
                      <div className="col-md-9 t-mb-15 mb-md-0">
                        <div className="input-group">
                          <div className="form-file">
                            <input
                              ref={searchRef}
                              type="text"
                              className="form-control border-0 form-control--light-1 rounded-left"
                              placeholder={_t(t("Search by branch or section..."))}
                              onChange={handleSearch}
                              disabled={loading || dataPaginating}
                            />
                          </div>
                          <button className="btn btn-primary rounded-right" type="button">
                            <i className="fa fa-search" aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>
                      <div className="col-md-3 text-md-right">
                        <NavLink
                          to="/dashboard/audit/rcr-add"
                          className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                        >
                          {_t(t("add new"))}
                        </NavLink>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DataTable
                loading={loading || dataPaginating}
                data={getListData()}
                columns={COLUMNS}
                emptyText="No data available"
                pagination={{
                  isPageLoading: loading,
                  isPagination: true,
                  pageCount: rcrAuditList?.last_page,
                  currentPage: rcrAuditList?.current_page,
                  from: rcrAuditList?.from,
                  to: rcrAuditList?.to,
                  total: rcrAuditList?.total,
                }}
                handleFetchData={handleFetchData}
                searchPayload={{
                  isSearched: searchedRcrAudit.searched,
                  onClearSearch
                }}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Modal for Qualitative Fields */}
      <Modal 
        show={showModal} 
        onHide={() => !isSubmittingReport && setShowModal(false)} 
        size="lg" 
        centered
        backdrop={isSubmittingReport ? "static" : true}
        keyboard={!isSubmittingReport}
      >
        <Modal.Header closeButton={!isSubmittingReport}>
          <Modal.Title className="mb-0">Generate Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-12 mb-3">
              <label className="font-weight-bold">Audit Observations<span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows="3"
                value={modalData?.qualitative?.audit_features || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setModalData(prev => ({
                    ...prev,
                    qualitative: { ...(prev?.qualitative || {}), audit_features: val }
                  }));
                }}
                placeholder="Enter audit observations..."
              />
            </div>
             <div className="col-12 mb-3">
              <label className="font-weight-bold">Exceptions <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows="3"
                value={modalData?.qualitative?.exceptions || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setModalData(prev => ({
                    ...prev,
                    qualitative: { ...(prev?.qualitative || {}), exceptions: val }
                  }));
                }}
                placeholder="Enter exceptions..."
              />
            </div>
            <div className="col-12 mb-3">
              <label className="font-weight-bold">Appreciations & Improvements</label>
              <textarea
                className="form-control"
                rows="3"
                value={modalData?.qualitative?.appreciations_improvements || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setModalData(prev => ({
                    ...prev,
                    qualitative: { ...(prev?.qualitative || {}), appreciations_improvements: val }
                  }));
                }}
                placeholder="Enter appreciations and improvements..."
              />
            </div>
          </div>

          {/* Modern UI Disclaimer Box */}
          <div className="alert mt-2 mb-1 shadow-sm" style={{ backgroundColor: '#fff5f5', border: '1px solid #fed7d7', borderLeft: '4px solid #e53e3e', borderRadius: '0.375rem', padding: '1rem' }}>
            <div className="d-flex">
              <div className="mr-3 mt-1">
                <i className="las la-exclamation-circle" style={{ fontSize: '24px', color: '#e53e3e' }}></i>
              </div>
              <div>
                <h6 className="font-weight-bold mb-1" style={{ color: '#9b2c2c', letterSpacing: '0.025em' }}>IMPORTANT DISCLAIMER:</h6>
                <p className="mb-0" style={{ fontSize: '0.875rem', lineHeight: '1.5', color: '#c53030' }}>
                  Once this form/report is saved and closed, it will be treated as a final record and cannot be edited, modified, or deleted. Please carefully review and verify all information, observations, attachments, and entries before clicking “Save & Close.”
                </p>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary rounded-sm" onClick={() => setShowModal(false)} disabled={isSubmittingReport}>
            Close
          </button>
          <button 
            className="btn btn-success rounded-sm" 
            onClick={submitGenerateReport}
            disabled={
              isSubmittingReport || 
              !(modalData?.qualitative?.audit_features || "").trim() || 
              !(modalData?.qualitative?.exceptions || "").trim()
            }
          >
            {isSubmittingReport ? (
              <>
                <i className="fa fa-spinner fa-spin mr-2"></i> Sending...
              </>
            ) : (
              "Complete"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RcrList;
